import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export const WORKSPACE_STORAGE_KEY = "nexus:demo-workspace";

export type WorkspaceId = "analyst" | "canal-france" | "canal-africa" | "telefonica-spain";

export type WorkspaceAudience =
  | { kind: "analyst" }
  | { kind: "client-demo"; tenantId: string; tenantLabel: string };

export interface WorkspaceDefinition {
  id: WorkspaceId;
  label: string;
  audience: WorkspaceAudience;
}

export const DEMO_WORKSPACES: readonly WorkspaceDefinition[] = [
  { id: "analyst", label: "Nagra Analyst", audience: { kind: "analyst" } },
  {
    id: "canal-france",
    label: "Canal+ France",
    audience: { kind: "client-demo", tenantId: "1", tenantLabel: "Canal+ France" },
  },
  {
    id: "canal-africa",
    label: "Canal+ Africa",
    audience: { kind: "client-demo", tenantId: "2", tenantLabel: "Canal+ Africa" },
  },
  {
    id: "telefonica-spain",
    label: "Telefonica Spain",
    audience: { kind: "client-demo", tenantId: "3", tenantLabel: "Telefonica Spain" },
  },
] as const;

const DEFAULT_WORKSPACE = DEMO_WORKSPACES[0];
const IS_DEMO_SWITCHER_ENABLED = false;
import { uiConfig, type SectionId } from "@/config/uiConfig";

export function resolveWorkspace(
  storedId: string | null,
  demoSwitcherEnabled: boolean,
): WorkspaceDefinition {
  if (!demoSwitcherEnabled) return DEFAULT_WORKSPACE;

  return DEMO_WORKSPACES.find((workspace) => workspace.id === storedId) ?? DEFAULT_WORKSPACE;
}

export function getWorkspaceSectionIds(audience: WorkspaceAudience): readonly SectionId[] {
  return audience.kind === "client-demo" ? ["dashboards"] : uiConfig.enabledSections;
}

export function getWorkspaceLandingPath(_audience: WorkspaceAudience): string {
  return "/home";
}
function readWorkspace(): WorkspaceDefinition {
  try {
    return resolveWorkspace(localStorage.getItem(WORKSPACE_STORAGE_KEY), IS_DEMO_SWITCHER_ENABLED);
  } catch {
    return DEFAULT_WORKSPACE;
  }
}

interface WorkspaceAudienceContextValue {
  workspace: WorkspaceDefinition;
  audience: WorkspaceAudience;
  workspaces: readonly WorkspaceDefinition[];
  isDemoSwitcherEnabled: boolean;
  setWorkspaceId: (workspaceId: WorkspaceId) => void;
}

const WorkspaceAudienceContext = createContext<WorkspaceAudienceContextValue | null>(null);

export function WorkspaceAudienceProvider({
  children,
  demoSwitcherEnabled = IS_DEMO_SWITCHER_ENABLED,
}: {
  children: ReactNode;
  demoSwitcherEnabled?: boolean;
}) {
  const [workspace, setWorkspace] = useState(() => {
    if (demoSwitcherEnabled === IS_DEMO_SWITCHER_ENABLED) return readWorkspace();

    try {
      return resolveWorkspace(localStorage.getItem(WORKSPACE_STORAGE_KEY), demoSwitcherEnabled);
    } catch {
      return DEFAULT_WORKSPACE;
    }
  });

  const setWorkspaceId = useCallback(
    (workspaceId: WorkspaceId) => {
      const nextWorkspace = resolveWorkspace(workspaceId, demoSwitcherEnabled);

      try {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, nextWorkspace.id);
      } catch {
        /* ignore */
      }

      setWorkspace(nextWorkspace);
    },
    [demoSwitcherEnabled],
  );

  const value = useMemo<WorkspaceAudienceContextValue>(
    () => ({
      workspace,
      audience: workspace.audience,
      workspaces: demoSwitcherEnabled ? DEMO_WORKSPACES : [DEFAULT_WORKSPACE],
      isDemoSwitcherEnabled: demoSwitcherEnabled,
      setWorkspaceId,
    }),
    [demoSwitcherEnabled, setWorkspaceId, workspace],
  );

  return (
    <WorkspaceAudienceContext.Provider value={value}>{children}</WorkspaceAudienceContext.Provider>
  );
}

export function useWorkspaceAudience() {
  const context = useContext(WorkspaceAudienceContext);

  if (!context) {
    throw new Error("useWorkspaceAudience must be used within a WorkspaceAudienceProvider");
  }

  return context;
}

export function useOptionalWorkspaceAudience() {
  return useContext(WorkspaceAudienceContext);
}
